import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Stack,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { adminAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import PortalLayout from '../../components/PortalLayout';

const DEFAULT_CONFIG = {
  inbound_types: [
    'Bestellannahme',
    'Helpdesk / Technische Hotline',
    'Sekretariatsservice',
    'Reservierung / Ticketing',
    'Notfallservice / 24h-Hotline',
    'Sonstiges',
  ],
  outbound_types: [
    'Telesales (Verkauf am Telefon)',
    'Terminvereinbarung',
    'Leadmanagement / Leadgenerierung',
    'Adressqualifizierung',
    'Kundenzufriedenheitsbefragung',
    'Marktforschung / Mystery Calls',
    'Kundenrückgewinnung',
    'Sonstiges',
  ],
  service_hours: [
    'Mo. – Fr. normale Bürozeiten (9–18 Uhr)',
    'Mo. – Fr. + Samstag',
    'Mo. – Fr. + Wochenende',
    '24 Stunden / 7 Tage pro Woche',
    'Individuell / Projektabhängig',
  ],
  step_labels: ['Art des Projekts', 'Projektdetails', 'Projektinfo', 'Ihre Kontaktdaten'],
  hero_headline: 'Kostenloses Gesuch aufgeben',
  hero_subline: 'In 4 Schritten zum passenden Call-Center-Partner – kostenlos und unverbindlich.',
};

interface TabPanelProps { children: React.ReactNode; value: number; index: number; }
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>{value === index && <Box sx={{ pt: 3 }}>{children}</Box>}</div>
);

const ListEditor: React.FC<{
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
}> = ({ label, items, onChange }) => {
  const [newItem, setNewItem] = useState('');

  const add = () => {
    if (!newItem.trim()) return;
    onChange([...items, newItem.trim()]);
    setNewItem('');
  };

  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx));

  const update = (idx: number, val: string) =>
    onChange(items.map((item, i) => (i === idx ? val : item)));

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>{label}</Typography>
      <List dense sx={{ border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}>
        {items.map((item, idx) => (
          <ListItem key={idx} divider={idx < items.length - 1}>
            <DragIndicatorIcon sx={{ color: '#ccc', mr: 1, fontSize: 18 }} />
            <TextField
              value={item}
              onChange={(e) => update(idx, e.target.value)}
              size="small"
              fullWidth
              variant="standard"
              InputProps={{ disableUnderline: true }}
            />
            <ListItemSecondaryAction>
              <IconButton size="small" onClick={() => remove(idx)} color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
        {items.length === 0 && (
          <ListItem>
            <ListItemText primary={<Typography color="textSecondary" variant="body2">Keine Einträge</Typography>} />
          </ListItem>
        )}
      </List>
      <Stack direction="row" gap={1}>
        <TextField
          size="small"
          placeholder="Neuen Eintrag hinzufügen..."
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          fullWidth
        />
        <Button variant="outlined" startIcon={<AddIcon />} onClick={add} sx={{ flexShrink: 0 }}>
          Hinzufügen
        </Button>
      </Stack>
    </Box>
  );
};

const AdminFormConfigPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [notifyEmail, setNotifyEmail] = useState('');
  const { showSuccess, showError } = useToast();

  const loadSettings = useCallback(async () => {
    try {
      const res = await adminAPI.getSettings();
      const s = res.data.data;
      if (s.form_config) {
        try {
          const parsed = JSON.parse(s.form_config);
          setConfig({ ...DEFAULT_CONFIG, ...parsed });
        } catch { /* keep default */ }
      }
      if (s.callback_notification_email) {
        setNotifyEmail(s.callback_notification_email);
      }
    } catch {
      showError('Fehler beim Laden der Einstellungen');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSaveFormConfig = async () => {
    setIsSaving(true);
    try {
      await adminAPI.setSettings({
        form_config: JSON.stringify(config),
        callback_notification_email: notifyEmail,
      });
      showSuccess('Einstellungen gespeichert');
    } catch {
      showError('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const updateConfig = (key: keyof typeof config, value: any) =>
    setConfig((prev) => ({ ...prev, [key]: value }));

  if (isLoading) {
    return (
      <PortalLayout>
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Formular-Konfigurator
        </Typography>
        <Button
          variant="contained"
          onClick={handleSaveFormConfig}
          disabled={isSaving}
          sx={{ px: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
        >
          {isSaving ? <CircularProgress size={20} /> : 'Alle Einstellungen speichern'}
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        Änderungen hier werden sofort für neue Anfragen über das Gesuch-Formular wirksam.
        Das Frontend liest die Konfiguration beim Laden der Seite aus.
      </Alert>

      <Paper sx={{ p: 0 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: '1px solid #e0e0e0', px: 2 }}>
          <Tab label="Formular-Texte" />
          <Tab label="Inbound-Typen" />
          <Tab label="Outbound-Typen" />
          <Tab label="Servicezeiten" />
          <Tab label="E-Mail-Einstellungen" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          <TabPanel value={tabValue} index={0}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Schritt-Beschriftungen (4 Schritte)
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Diese Labels erscheinen im Stepper oben im Gesuch-Formular.
            </Typography>
            <Stack gap={2} sx={{ mb: 4 }}>
              {config.step_labels.map((label, idx) => (
                <TextField
                  key={idx}
                  label={`Schritt ${idx + 1}`}
                  value={label}
                  onChange={(e) => {
                    const updated = [...config.step_labels];
                    updated[idx] = e.target.value;
                    updateConfig('step_labels', updated);
                  }}
                  size="small"
                  fullWidth
                />
              ))}
            </Stack>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Hero-Bereich (Formular-Seite)
            </Typography>
            <Stack gap={2}>
              <TextField
                label="Überschrift"
                value={config.hero_headline}
                onChange={(e) => updateConfig('hero_headline', e.target.value)}
                fullWidth
              />
              <TextField
                label="Unterzeile"
                value={config.hero_subline}
                onChange={(e) => updateConfig('hero_subline', e.target.value)}
                fullWidth
              />
            </Stack>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <ListEditor
              label="Inbound-Typen (erscheinen bei Auswahl 'Inbound')"
              items={config.inbound_types}
              onChange={(items) => updateConfig('inbound_types', items)}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <ListEditor
              label="Outbound-Typen (erscheinen bei Auswahl 'Outbound')"
              items={config.outbound_types}
              onChange={(items) => updateConfig('outbound_types', items)}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <ListEditor
              label="Servicezeiten-Optionen"
              items={config.service_hours}
              onChange={(items) => updateConfig('service_hours', items)}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              E-Mail-Benachrichtigung für Rückrufanfragen
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              An diese E-Mail-Adresse wird eine Benachrichtigung gesendet, wenn eine neue Rückrufanfrage eingeht.
            </Typography>
            <TextField
              label="Benachrichtigungs-E-Mail"
              type="email"
              value={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.value)}
              fullWidth
              size="small"
              placeholder="admin@callcenter-fuchs.de"
              helperText="Leer lassen, um keine Benachrichtigungen zu erhalten"
            />
          </TabPanel>
        </Box>
      </Paper>
    </PortalLayout>
  );
};

export default AdminFormConfigPage;
